import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import actions from '@/actions';
import { getWorkspaceId, yabbrAuthRedirect } from '@/utils';

export const routes = [
  {
    path: '/',
    name: 'Home',
    meta: {
      requires: ['sessionToken'],
    },
    component: () => import(/* webpackChunkName: "credentials" */ '@/components/app/HomePage.vue'),
  },
  {
    path: '/credentials',
    name: 'Credential List',
    meta: {
      requires: ['sessionToken'],
      breadcrumb: [{
        title: 'Credentials',
        to: { name: 'Credential List' },
      }],
    },
    component: () => import(/* webpackChunkName: "credentials" */ '@/components/credentials/CredentialList.vue'),
  },
  {
    path: '/time-conditions',
    name: 'Time Condition Lists',
    meta: {
      requires: ['sessionToken'],
      breadcrumb: [{
        title: 'Time Conditions',
        to: { name: 'Time Condition Lists' },
      }],
    },
    component: () => import(/* webpackChunkName: "timeouts" */ '@/components/timeouts/TimeoutList.vue'),
  },
  {
    path: '/time-conditions/:listId',
    name: 'Time Condition List Detail',
    meta: {
      requires: ['sessionToken'],
      breadcrumbResource: 'timeoutList',
      breadcrumb: [
        {
          title: 'Time Conditions',
          to: { name: 'Time Condition Lists' },
          exact: true,
        },
        {
          title: { param: 'listId', property: 'name', default: 'List' },
          to: { name: 'Time Condition List Detail' },
        },
      ],
    },
    component: () => import(/* webpackChunkName: "timeouts" */ '@/components/timeouts/TimeoutListDetail.vue'),
  },
  {
    path: '/domains',
    name: 'Namespaces',
    meta: {
      requires: ['sessionToken'],
      breadcrumb: [{
        title: 'Namespaces',
        to: { name: 'Namespaces' },
      }],
    },
    component: () => import(/* webpackChunkName: "domain" */ '@/components/dms/DomainList.vue'),
  },
  {
    path: '/workflows',
    name: 'Workflow List',
    meta: {
      requires: ['sessionToken'],
      breadcrumb: [{
        title: 'Workflows',
        to: { name: 'Workflow List' },
      }],
    },
    component: () => import(/* webpackChunkName: "workflows" */ '@/components/workflows/WorkflowList.vue'),
  },
  {
    path: '/workflows/:workflowId/chart',
    name: 'Workflow Chart',
    meta: {
      requires: ['sessionToken'],
      breadcrumbResource: 'workflowDetail',
      breadcrumb: [
        {
          title: 'Workflows',
          to: { name: 'Workflow List' },
          exact: true,
        },
        {
          title: { param: 'workflowId', property: 'name', default: 'Workflow' },
          to: { name: 'Workflow Chart' },
          exact: true,
        },
      ],
    },
    component: () => import(/* webpackChunkName: "workflows" */ '@/components/workflows/WorkflowChart.vue'),
  },
  {
    path: '/schedules',
    name: 'Scheduled Event List',
    meta: {
      requires: ['sessionToken'],
      breadcrumb: [{
        title: 'Schedules',
        to: { name: 'Scheduled Event List' },
      }],
    },
    component: () => import(/* webpackChunkName: "events" */ '@/components/schedule/ScheduleList.vue'),
  },
  {
    path: '/audits',
    name: 'Workflow Audits',
    meta: {
      requires: ['sessionToken'],
      breadcrumb: [
        {
          title: 'Workflow Monitors',
          to: { name: 'Workflow Audits' },
        },
      ],
    },
    component: () => import(/* webpackChunkName: "events" */ '@/components/audits/AuditList.vue'),
  },
  {
    path: '/audits/:auditId',
    name: 'Workflow Audit',
    meta: {
      requires: ['sessionToken'],
      breadcrumbResource: 'auditDetail',
      breadcrumb: [
        {
          title: 'Workflow Monitors',
          to: { name: 'Workflow Audits' },
          exact: true,
        },
        {
          title: { param: 'auditId', property: 'name', default: 'Monitor' },
          to: { name: 'Workflow Audit' },
          exact: true,
        },
      ],
    },
    component: () => import(/* webpackChunkName: "workflows" */ '@/components/audits/AuditDetail.vue'),
  },
  {
    path: '/workflows/:workflowId/logs',
    name: 'Workflow Instances',
    meta: {
      requires: ['sessionToken'],
      breadcrumbResource: 'workflowDetail',
      breadcrumb: [
        {
          title: 'Workflows',
          to: { name: 'Workflow List' },
          exact: true,
        },
        {
          title: { param: 'workflowId', property: 'name', default: 'Workflow' },
          to: { name: 'Workflow Chart' },
          exact: true,
        },
        {
          title: 'Instances',
          to: { name: 'Workflow Instances' },
          exact: true,
        },
      ],
    },
    component: () => import(/* webpackChunkName: "workflows" */ '@/components/workflows/WorkflowInstances.vue'),
  },
  {
    path: '/workflows/:workflowId/test',
    name: 'Workflow Test',
    meta: {
      requires: ['sessionToken'],
      breadcrumbResource: 'workflowDetail',

      breadcrumb: [
        {
          title: 'Workflows',
          to: { name: 'Workflow List' },
          exact: true,
        },
        {
          title: { param: 'workflowId', property: 'name', default: 'Workflow' },
          to: { name: 'Workflow Chart' },
          exact: true,
        },
        {
          title: 'Testing',
          to: { name: 'Workflow Test' },
          exact: true,
        },
      ],
    },
    component: () => import(/* webpackChunkName: "workflows" */ '@/components/workflows/WorkflowTesting.vue'),
  },
  {
    path: '/workflows/:workflowId/logs/:instanceId',
    name: 'Instance Logs',
    meta: {
      requires: ['sessionToken'],
      breadcrumbResource: 'workflowDetail',
      breadcrumb: [
        {
          title: 'Workflows',
          to: { name: 'Workflow List' },
          exact: true,
        },
        {
          title: { param: 'workflowId', property: 'name', default: 'Workflow' },
          to: { name: 'Workflow Chart' },
          exact: true,
        },
        {
          title: 'Instances',
          to: { name: 'Workflow Instances' },
          exact: true,
        },
        {
          title: { param: 'instanceId', default: 'Instance' },
          to: { name: 'Instance Logs' },
          exact: true,
        },
      ],
    },
    component: () => import(/* webpackChunkName: "workflows" */ '@/components/workflows/InstanceLogs.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import(/* webpackChunkName: "404" */ '@/components/app/NotFound.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  if (to.query.action) {
    if (to.query.token) {
      localStorage.setItem('yabbr-token', to.query.token);
    }

    if (to.query.organisationId) {
      localStorage.setItem('yabbr-organisation', to.query.organisationId);
    }

    if (to.query.mfa) {
      localStorage.setItem('yabbr-mfa', to.query.mfa);
    }

    router.replace({ path: to.path, query: {} });
  }

  const reqs = [
    ...new Set(
      to.matched.reduce((acc, cv) => (acc.concat(cv.meta.requires || [])), []),
    ),
  ];

  store.dispatch(actions.setCurrentRoute(to));

  if (!store.getState().user && localStorage.getItem('yabbr-token')) {
    store.dispatch(actions.addLoad());
    try {
      await store.dispatch(actions.appInit());
    } catch (e) {
      console.log(e);
      store.dispatch(actions.endSession());
      yabbrAuthRedirect();
    } finally {
      store.dispatch(actions.removeLoad());
    }
  }

  if (reqs.indexOf('sessionToken') !== -1 && !localStorage.getItem('yabbr-token')) {
    yabbrAuthRedirect();
  }

  if (reqs.indexOf('activeWorkspaceId') !== -1
    && !getWorkspaceId()) {
    // we dont have a workspace ID in storage
    // the token has been deleted, or they have no workspaces in Yabbr.
    // the user has to choose a workspace to use the app, so take them to yabbr to select one.
    yabbrAuthRedirect();
  }

  return next();
});

export default router;
